import type { DebugContextType } from "../../hooks/DebugProvider";
import { Checkbox, cn, Select, Typography } from "@vintecc/components";
import { FC, useMemo, useState } from "react";
import { useDebugStore } from "../../store/debugStore";

const getValue = (value: string | number | boolean) => {
  if (typeof value === "boolean") {
    return value ? "true" : "false";
  }
  return value;
};

const defaultLocalPort = "5173";

const environmentMap = {
  local: "localhost",
  development: "viniti.dev.vintecc.cloud",
  staging: "portal.demo.captureplatform.com",
  // production: "capture.cloud",
};

const environmentOptions = [
  { value: "local", label: "Local" },
  { value: "development", label: "Development" },
  { value: "staging", label: "Demo" },
  { value: "production", label: "Production", disabled: true },
];

const getCurrentEnvironment = (): keyof typeof environmentMap => {
  const host = window.location.host;
  const envEntry = Object.entries(environmentMap).find(([_, url]) => host.includes(url));
  return envEntry ? (envEntry[0] as keyof typeof environmentMap) : "local";
};

const getLocation = (searchOverride?: string) => {
  const { pathname } = window.location;
  return `${pathname}${searchOverride ?? window.location.search}`;
};

const getPort = () => {
  const port = window.location.port;
  return port ? `:${port}` : `:${defaultLocalPort}`;
};

export const DebugGeneral: FC<DebugContextType> = ({ variables }) => {
  // Inside DebugGeneral (or DebugModal)
  const showKeys = useDebugStore((s) => s.showKeys);
  const setShowKeys = useDebugStore((s) => s.setShowKeys);
  const [currentEnvironment, setCurrentEnvironment] = useState<keyof typeof environmentMap>(getCurrentEnvironment());

  const { currentProtocol, port } = useMemo(() => {
    if (currentEnvironment === "local") {
      return { currentProtocol: "http", port: getPort() };
    }
    return { currentProtocol: "https", port: "" };
  }, [currentEnvironment]);

  const additionalSearchParams = useMemo(() => {
    const params = new URLSearchParams(window.location.search); // start from existing params
    if (showKeys) {
      params.set("showKeys", "true");
    } else {
      params.delete("showKeys");
    }
    const qs = params.toString();
    return qs ? `?${qs}` : "";
  }, [showKeys]);

  return (
    <div className="flex flex-col gap-4" data-testid="debug-general">
      <div className="flex flex-col gap-4 py-4">
        <Typography variant="h3">Current Variables</Typography>
        {variables && (
          <dl>
            {Object.entries(variables).map(([key, value]) => (
              <div key={key} className={cn("flex gap-2 justify-between", "bg-grey-100 p-2 rounded", "even:bg-white")}>
                <dt>
                  <Typography variant="strong" className="text-start">
                    {key}
                  </Typography>
                </dt>
                <dd className="text-end">{getValue(value)}</dd>
              </div>
            ))}
          </dl>
        )}
      </div>

      <div className="flex flex-col gap-4">
        <Typography variant="h3">Debug Options</Typography>
        <Checkbox
          name="showTranslationKeys"
          label="Show Translation Keys"
          checked={showKeys}
          onChange={(checked) => setShowKeys(checked)}
          data-testid="debug-show-keys-checkbox"
        />
        <div className="border border-grey-200 bg-grey-100 px-2 py-2 rounded">
          <Select
            label="Environment"
            name="environment"
            options={environmentOptions}
            value={currentEnvironment}
            onSelect={(value) => setCurrentEnvironment(value.value as keyof typeof environmentMap)}
          />
          <a
            href={`${currentProtocol}://${environmentMap[currentEnvironment]}${port}${getLocation(additionalSearchParams)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-(--theme-default-color) hover:underline p-2 block"
          >
            {currentProtocol}://{environmentMap[currentEnvironment]}
            {port}
            {getLocation(additionalSearchParams)}
          </a>
        </div>
      </div>
      <div className="flex flex-col gap-4">
        <Typography variant="h3">Shortcuts</Typography>
        <Typography variant="p">
          <strong className="border border-grey-200 bg-grey-100 px-1 py-0.5 rounded-lg">Space + D</strong> Open Debug Modal
        </Typography>
      </div>
    </div>
  );
};
